import React, {forwardRef, useImperativeHandle, useState} from 'react';
import PropTypes from 'prop-types';
import {Modal, Spin, message, Radio} from 'antd';
import _ from 'lodash';
import MainPanel from './MainPanel';
import SelectedPanel from './SelectedPanel';
import {isPromise} from '@/util/PromiseUtils';
import styles from './index.module.less';

const UserSelect = forwardRef(({modalWidth = 1200, onSelect, isMoment = false}, ref) => {
    const [visible, setVisible] = useState(false);
    const [title, setTitle] = useState('选择范围');
    const [spinning, setSpinning] = useState(false);
    // 格式为 {users: [{id, name}], groups: [{id, name}]}
    const [selectedValues, setSelectedValues] = useState(null);
    const [userId, setUserId] = useState(null);
    // 1 为 发送人 2 为发送群
    const [sendGroupType, setSendGroupType] = useState(1);
    const [sendType, setSendType] = useState(1);

    /**
     * userType 'singleCustomer' 为客户 'singleSender' 'group' 为汇报线人员
     * 打开 Modal，同时可以传入的相应的参数
     *{modalTitle, initialValues} = {}
     * @param modalTitle Modal 标题，不传，则使用 props 中的值
     * @param initialValues 初始选择项，格式为 {users: [{id, name}], groups: [{id, name}]}
     */
    function showModal(userId, selectedUsers, sendType) {
        setSelectedValues(selectedUsers);
        setSendType(sendType);
        if (userId) {
            setUserId(userId);
        }
        setVisible(true);
    }

    function closeModal() {
        setUserId(null);
        setSelectedValues(null);
        setVisible(false);
        setSpinning(false);
    }

    useImperativeHandle(ref, () => ({
        showModal,
        closeModal,
    }));

    function changeSendGroupType(e) {
        // 移除虽有所选项
        setSelectedValues(null);
        onSelect(userId, null, sendGroupType);
        setSendGroupType(e.target.value);
    }

    function onAddSelected(type, {id, name}) {
        let values = _.get(selectedValues, type, []);
        const value = values.find(item => item.id === id);
        if (!value) {
            values = [...values, {id, name}];
            setSelectedValues({...selectedValues, [type]: values});
        }
    }

    function onDeleteSelected(type, delId) {
        let values = _.get(selectedValues, type, []);
        values = values.filter(({id}) => id !== delId);
        setSelectedValues({...selectedValues, [type]: values});
    }

    function onOk() {
        // 将返回值中的 undefined 置为 []
        const values = {};
        if (_.isEmpty(selectedValues)) {
            message.error('未选择任何用户');
            return;
        }
        setSpinning(true);
        Object.keys(selectedValues).forEach(key => {
            const arr = selectedValues[key];
            values[key] = arr || [];
        });

        const result = onSelect(userId, values, sendGroupType);
        if (isPromise(result)) {
            result.then(data => {
                if (data === true) {
                    closeModal();
                } else {
                    setSpinning(false);
                }
            });
        } else {
            if (result) {
                closeModal();
            } else {
                setSpinning(false);
            }
        }
    }

    return (
        <Modal
            destroyOnClose
            title={title}
            visible={visible}
            onCancel={closeModal}
            onOk={onOk}
            width={modalWidth}
        >
            <Spin spinning={spinning}>
                <Radio.Group
                    value={sendGroupType}
                    onChange={changeSendGroupType}
                    style={{display: sendType == 1 ? 'none' : 'block'}}
                >
                    <Radio value={1}>发送客户</Radio>
                    {!isMoment && <Radio value={2}>发送客户群</Radio>}
                </Radio.Group>
                <div className={styles.container}>
                    <div className={styles.mainPanel}>
                        <MainPanel onAddSelected={onAddSelected} selectUserId={userId} />
                    </div>
                    <div className={styles.selectedPanel}>
                        <SelectedPanel
                            selectedValues={selectedValues}
                            onDeleteSelected={onDeleteSelected}
                        />
                    </div>
                </div>
            </Spin>
        </Modal>
    );
});

UserSelect.displayName = 'UserSelect';

UserSelect.propTypes = {
    modalTitle: PropTypes.string,

    modalWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    /**
     * 点击「确定」按钮后触发，返回 true 的时候关闭 Modal
     *
     * 支持返回 Promise
     */
    onSelect: PropTypes.func.isRequired,
};

export default UserSelect;
